import { WelcomeCertificate } from "@/components/WelcomeCertificate";
import { PushToggle } from "@/components/PushToggle";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ShieldAlert, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHead, StatusBadge, EmptyState, Stat } from "@/components/common";
import { ApptRow } from "@/pages/patient/PatientPages";
import { EarningsStatement, PayoutBanner } from "@/components/Payouts";
import { FullLoader, useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDT, fmtTime, fmtDate, inr, useApi, BACKEND } from "@/lib/api";

export function VerificationGate({ children }) {
  const { user } = useAuth();
  const st = user?.doctor?.verification_status;
  if (st === "approved") return children;
  return (
    <div className="surface p-8 max-w-xl" data-testid="verification-gate">
      <ShieldAlert className="h-6 w-6 text-[#FF9700]" />
      <h2 className="mt-4 text-xl font-semibold">Verification {st === "pending" ? "in progress" : "required"}</h2>
      <p className="mt-2 text-sm muted">{st === "pending" ? "Our team is reviewing your registration and documents. You'll be notified by email." : st === "suspended" ? "Your account is suspended. Please contact support." : "Complete your professional profile and upload your registration certificate to unlock consultations."}</p>
      <div className="mt-3"><StatusBadge status={st} testId="doctor-verification-status" /></div>
      <Button asChild className="mt-6 rounded-full"><Link to="/doctor/profile" data-testid="gate-profile-link">Go to profile & verification</Link></Button>
    </div>
  );
}

export function DoctorHome() {
  const { data, loading } = useApi("/doctor/dashboard");
  const nav = useNavigate();
  if (loading || !data) return <FullLoader />;
  return (
    <div>
      <PageHead eyebrow="Doctor workspace" title={`Good day, ${data.profile.full_name}`} sub="Today's schedule in IST." />
      <PushToggle compact />
      <WelcomeCertificate />
      <PayoutBanner />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Today's consultations" value={data.today.length} testId="stat-today" />
        <Stat label="Upcoming (next days)" value={data.upcoming.length} testId="stat-upcoming" />
        <Stat label="Completed" value={data.completed_count} testId="stat-completed" />
        <Stat label="Net earnings (paid)" value={inr(data.earnings.net_earnings)} testId="stat-earnings" />
      </div>
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 surface p-6" data-testid="today-schedule">
          <h3 className="font-semibold mb-4">Today's schedule</h3>
          {data.today.length === 0 ? <EmptyState title="No consultations today" /> : data.today.map((a) => (
            <button key={a.id} onClick={() => nav(`/doctor/appointments/${a.id}`)} className="w-full flex items-center gap-4 py-3 border-b border-[#E2E8ED] last:border-0 text-left hover:bg-[#F7F9FA] px-2 -mx-2 rounded-lg" data-testid={`today-appt-${a.id}`}>
              <span className="font-display text-base w-24 shrink-0 whitespace-nowrap text-[#2412B5]">{fmtTime(a.start_at)}</span>
              <span className="flex-1 min-w-0"><span className="block text-sm font-medium truncate">{a.patient_name}</span><span className="text-xs muted capitalize">{a.mode} · {a.ref}</span></span>
              <StatusBadge status={a.status} />
            </button>
          ))}
        </section>
        <section className="surface p-6"><h3 className="font-semibold mb-3">Upcoming</h3>
          {data.upcoming.length === 0 ? <p className="text-sm muted">Nothing scheduled.</p> : data.upcoming.map((a) => <ApptRow key={a.id} a={{ ...a, doctor_name: a.patient_name }} to={`/doctor/appointments/${a.id}`} />)}
        </section>
      </div>
      <DoctorFollowUps />
    </div>
  );
}

function DoctorFollowUps() {
  const { data, reload } = useApi("/follow-ups");
  const remind = async (id) => { try { const { data: r } = await api.post(`/follow-ups/${id}/remind`); toast.success(`Reminder sent (${r.reminders_sent}/3)`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const items = (data || []).filter((f) => f.status === "pending");
  return (
    <section className="surface p-6 mt-6" data-testid="doctor-followups">
      <h3 className="font-semibold mb-3">Pending follow-ups</h3>
      {items.length === 0 ? <p className="text-sm muted">No pending follow-ups.</p> : items.map((f) => (
        <div key={f.id} className="flex items-center justify-between gap-3 py-2 border-b border-[#E2E8ED] last:border-0 text-sm">
          <span>{f.patient_name} · window closes {fmtDT(f.expires_at)}</span>
          <Button size="sm" variant="outline" disabled={(f.doctor_reminders || 0) >= 3} onClick={() => remind(f.id)} data-testid={`followup-remind-${f.id}`}>Send reminder ({f.doctor_reminders || 0}/3)</Button>
        </div>))}
    </section>
  );
}

export function DoctorAppointments() {
  const [scope, setScope] = useState("upcoming");
  const { data, loading } = useApi(`/appointments?scope=${scope}`);
  return (
    <div>
      <PageHead eyebrow="Appointments" title="Consultations" />
      <Tabs value={scope} onValueChange={setScope}><TabsList><TabsTrigger value="upcoming" data-testid="doc-appts-upcoming">Upcoming</TabsTrigger><TabsTrigger value="past" data-testid="doc-appts-past">Past</TabsTrigger><TabsTrigger value="all">All</TabsTrigger></TabsList></Tabs>
      <div className="surface p-5 mt-4">{loading ? <FullLoader /> : data?.length ? data.map((a) => <ApptRow key={a.id} a={{ ...a, doctor_name: a.patient_name }} to={`/doctor/appointments/${a.id}`} />) : <EmptyState title="No consultations" />}</div>
    </div>
  );
}

export function DoctorPrescriptions() {
  const { data, loading } = useApi("/prescriptions");
  return (
    <div>
      <PageHead eyebrow="Prescriptions" title="Prescription history" />
      <div className="surface p-5">{loading ? <FullLoader /> : (data || []).length === 0 ? <EmptyState title="No prescriptions yet" /> : data.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-3 py-3 border-b border-[#E2E8ED] last:border-0">
          <Link to={`/doctor/appointments/${p.appointment_id}`} className="min-w-0"><p className="text-sm font-medium">{p.rx_number} · {p.patient?.name}</p><p className="text-xs muted">v{p.version} · {fmtDate(p.finalized_at || p.created_at)}</p></Link>
          <div className="flex items-center gap-3"><StatusBadge status={p.status} /><a href={`${BACKEND}/api/prescriptions/${p.id}/pdf`} target="_blank" rel="noreferrer" aria-label="PDF"><FileText className="h-4 w-4 text-[#2412B5]" /></a></div>
        </div>))}</div>
    </div>
  );
}

export function DoctorEarnings() {
  return <><EarningsStatement title="Earnings & payouts" sub="Consultation fees from completed, paid consultations less 20% Nirogyam commission." /><div className="mt-10"><DoctorEarningsDetail /></div></>;
}

function DoctorEarningsDetail() {
  const { data, loading } = useApi("/doctor/earnings");
  if (loading || !data) return <FullLoader />;
  return (
    <div>
      <PageHead eyebrow="Earnings" title="Earnings statement" sub="Based on completed consultations with verified payments." />
      <div className="grid sm:grid-cols-4 gap-4">
        <Stat label="Paid consultations" value={data.paid_consultations} /><Stat label="Gross fees" value={inr(data.gross_fees)} />
        <Stat label="Platform commission" value={inr(data.platform_commission)} /><Stat label="Net earnings" value={inr(data.net_earnings)} testId="earnings-net" />
      </div>
      {data.unpaid_completed > 0 && <p className="mt-4 text-sm text-[#9A5B00]">{data.unpaid_completed} completed consultation(s) are awaiting patient payment and are not counted.</p>}
      <div className="surface mt-6 overflow-x-auto"><table className="w-full text-sm"><thead className="bg-[#F7F9FA] text-left text-xs muted"><tr><th className="p-3">Date</th><th className="p-3">Booking</th><th className="p-3">Patient</th><th className="p-3">Fee</th><th className="p-3">Net</th></tr></thead>
        <tbody>{data.statement.map((r) => <tr key={r.id} className="border-t border-[#E2E8ED]"><td className="p-3">{fmtDT(r.start_at)}</td><td className="p-3">{r.ref}</td><td className="p-3">{r.patient_name}</td><td className="p-3">{inr(r.fee)}</td><td className="p-3">{inr(r.fee * (1 - r.commission_percent / 100))}</td></tr>)}</tbody></table>
        {data.statement.length === 0 && <div className="p-6"><EmptyState title="No paid consultations yet" /></div>}</div>
    </div>
  );
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function DoctorAvailability() {
  const { data, loading } = useApi("/doctor/availability");
  const [a, setA] = useState(null);
  const [date, setDate] = useState("");
  useEffect(() => { if (data) setA({ weekly: data.weekly || {}, breaks: data.breaks || [], unavailable_dates: data.unavailable_dates || [] }); }, [data]);
  if (loading || !a) return <FullLoader />;
  const setWin = (d, i, k, v) => { const w = [...(a.weekly[d] || [])]; w[i] = { ...w[i], [k]: v }; setA({ ...a, weekly: { ...a.weekly, [d]: w } }); };
  const addWin = (d) => setA({ ...a, weekly: { ...a.weekly, [d]: [...(a.weekly[d] || []), { start: "09:00", end: "13:00" }] } });
  const delWin = (d, i) => setA({ ...a, weekly: { ...a.weekly, [d]: a.weekly[d].filter((_, j) => j !== i) } });
  const save = async () => { try { await api.put("/doctor/availability", a); toast.success("Availability saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHead eyebrow="Availability" title="Weekly schedule" sub={`Slot length: ${data.slot_duration} min (change in Profile). Times are IST.`} right={<Button className="rounded-full" onClick={save} data-testid="availability-save-btn">Save availability</Button>} />
      <div className="surface divide-y divide-[#E2E8ED]" data-testid="weekly-editor">
        {DAYS.map((name, d) => (
          <div key={d} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="w-28 text-sm font-medium">{name}</p>
            <div className="flex-1 flex flex-wrap gap-2">
              {(a.weekly[d] || []).length === 0 && <span className="text-sm muted">Unavailable</span>}
              {(a.weekly[d] || []).map((w, i) => (
                <div key={i} className="flex items-center gap-1 rounded-xl border border-[#E2E8ED] px-2 py-1">
                  <Input type="time" value={w.start} onChange={(e) => setWin(d, i, "start", e.target.value)} className="h-8 w-28 border-0" data-testid={`win-${d}-${i}-start`} />
                  <span className="muted">–</span>
                  <Input type="time" value={w.end} onChange={(e) => setWin(d, i, "end", e.target.value)} className="h-8 w-28 border-0" data-testid={`win-${d}-${i}-end`} />
                  <button onClick={() => delWin(d, i)} aria-label="Remove"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={() => addWin(d)} data-testid={`add-window-${d}`}><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
        ))}
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <section className="surface p-6"><h3 className="font-semibold">Daily breaks</h3>
          {a.breaks.map((b, i) => (
            <div key={i} className="mt-3 flex items-center gap-2"><Input type="time" value={b.start} onChange={(e) => setA({ ...a, breaks: a.breaks.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)) })} />
              <Input type="time" value={b.end} onChange={(e) => setA({ ...a, breaks: a.breaks.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)) })} />
              <button onClick={() => setA({ ...a, breaks: a.breaks.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4 text-red-600" /></button></div>))}
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setA({ ...a, breaks: [...a.breaks, { start: "13:00", end: "14:00" }] })} data-testid="add-break-btn">Add break</Button>
        </section>
        <section className="surface p-6"><h3 className="font-semibold">Unavailable dates</h3>
          <div className="mt-3 flex gap-2"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="unavailable-date-input" />
            <Button variant="outline" onClick={() => { if (date && !a.unavailable_dates.includes(date)) setA({ ...a, unavailable_dates: [...a.unavailable_dates, date].sort() }); setDate(""); }} data-testid="add-unavailable-btn">Add</Button></div>
          <div className="mt-3 flex flex-wrap gap-2">{a.unavailable_dates.map((d) => <button key={d} onClick={() => setA({ ...a, unavailable_dates: a.unavailable_dates.filter((x) => x !== d) })} className="rounded-full bg-[#F7F9FA] border border-[#E2E8ED] px-3 py-1 text-xs">{fmtDate(d)} ×</button>)}</div>
        </section>
      </div>
    </div>
  );
}
